import { Box, Chip, Typography, useTheme } from '@mui/material';
import HourRuler from './HourRuler';
import TimelineRow from './TimelineRow';
import { DAY_LABELS, LABEL_W, toMins } from './constants';


// Section header row — label is sticky-left, colored background spans the grid
const SectionHeader = ({ label, color, totalW }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        height: 22,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 2,
          width: LABEL_W,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRight: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          px: 0.75,
        }}
      >
        <Typography
          variant="overline"
          color={color}
          sx={{ fontSize: '0.6rem', fontWeight: 'bold', lineHeight: 1 }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ width: totalW, flexShrink: 0 }} />
    </Box>
  );
};


const StationTimeline = ({
  selectedStation,
  airportMap,
  filterDay,
  stationMovements,
  acTypeColorMap,
  rowHeight = 32,
  hourWidth = 56,
}) => {
  const theme  = useTheme();
  const totalW = 24 * hourWidth;
  const { dep, arr } = stationMovements;
  const ap = airportMap[selectedStation];

  if (!selectedStation) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <Typography color="text.secondary">Select a station</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      {/* Outer width constraint so sticky elements have correct scroll reference */}
      <Box sx={{ width: LABEL_W + totalW, minHeight: '100%' }}>

        {/* ── Sticky header: airport info + ruler ── */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 4,
            bgcolor: 'background.paper',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Airport info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
            <Box sx={{ width: LABEL_W - 8, flexShrink: 0 }}>
              <Typography variant="subtitle2" fontFamily="monospace" fontWeight="bold">
                {selectedStation}
              </Typography>
            </Box>
            {ap?.airportName && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                {ap.airportName}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {DAY_LABELS[Number(filterDay)]}
            </Typography>
            <Box display="flex" gap={0.5} flexShrink={0}>
              {Object.entries(acTypeColorMap).map(([t, c]) => (
                <Chip key={t} label={t} size="small" sx={{ bgcolor: c, color: '#fff', fontSize: '0.6rem' }} />
              ))}
            </Box>
          </Box>

          {/* Hour ruler */}
          <Box sx={{ display: 'flex' }}>
            <Box
              sx={{
                width: LABEL_W,
                flexShrink: 0,
                height: 28,
                bgcolor: 'background.dark',
                borderRight: `1px solid ${theme.palette.divider}`,
              }}
            />
            <HourRuler hourWidth={hourWidth} />
          </Box>
        </Box>

        {/* ── Departures ── */}
        <SectionHeader label={`DEP  ${dep.length}`} color="primary.main" totalW={totalW} />
        {dep.length === 0 ? (
          <Box sx={{ display: 'flex', height: rowHeight, alignItems: 'center', px: 1 }}>
            <Typography variant="caption" color="text.disabled">No departures</Typography>
          </Box>
        ) : (
          dep.map((f) => (
            <TimelineRow
              key={f._id}
              label={`${f.carrier}${f.flightNo}→${f.arr}`}
              flights={[f]}
              acTypeColorMap={acTypeColorMap}
              hourWidth={hourWidth}
              rowHeight={rowHeight}
            />
          ))
        )}

        {/* ── Arrivals ── */}
        <SectionHeader label={`ARR  ${arr.length}`} color="success.main" totalW={totalW} />
        {arr.length === 0 ? (
          <Box sx={{ display: 'flex', height: rowHeight, alignItems: 'center', px: 1 }}>
            <Typography variant="caption" color="text.disabled">No arrivals</Typography>
          </Box>
        ) : (
          arr.map((f) => (
            <TimelineRow
              key={f._id}
              label={`${f.carrier}${f.flightNo} ${f.dep}→`}
              flights={[f]}
              acTypeColorMap={acTypeColorMap}
              hourWidth={hourWidth}
              rowHeight={rowHeight}
            />
          ))
        )}

        {/* ── Turn Activity ── */}
        <SectionHeader label="TAT" color="text.secondary" totalW={totalW} />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', px: 1, py: 0.75 }}>
          {arr.map((arrF) => {
            const staM = toMins(arrF.sta);
            if (staM === null) return null;
            const nextDep = dep.find((d) => {
              const stdM = toMins(d.std);
              return stdM !== null && stdM > staM && d.acType === arrF.acType;
            });
            if (!nextDep) return null;
            const tat   = toMins(nextDep.std) - staM;
            const color = tat < 30 ? 'error' : tat < 60 ? 'warning' : 'success';
            return (
              <Chip
                key={`${arrF._id}-turn`}
                label={`${arrF.carrier}${arrF.flightNo}→${nextDep.carrier}${nextDep.flightNo} ${tat}m`}
                size="small"
                color={color}
                variant="outlined"
                sx={{ fontSize: '0.6rem' }}
              />
            );
          })}
          {arr.every((arrF) => {
            const staM = toMins(arrF.sta);
            if (staM === null) return true;
            return !dep.some((d) => {
              const stdM = toMins(d.std);
              return stdM !== null && stdM > staM && d.acType === arrF.acType;
            });
          }) && (
            <Typography variant="caption" color="text.disabled">No turns matched</Typography>
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default StationTimeline;
